import { ShieldBan, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../app/hooks";
import { AdminBlockResponse } from "../../features/admin/contracts/AdminBlockResponse";
import { createAdminBlock, removeAdminBlock } from "../../features/admin/slice-extensions/admin-moderation-slice-extension";
import AdminBlockDialog from "./AdminBlockDialog";

interface AdminBlockToggleProps {
  kind: AdminBlockResponse["kind"];
  principalId: string;
  name: string;
  block: AdminBlockResponse | null;
}

/** "Blocked" with the reason and Unblock, or Block with a reason dialog (US-111). */
const AdminBlockToggle = ({ kind, principalId, name, block: initial }: AdminBlockToggleProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [block, setBlock] = useState<AdminBlockResponse | null>(initial);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);

  const blockNow = async (reason: string) => {
    setBusy(true);
    const result = await dispatch(createAdminBlock({ kind, id: principalId, reason }));
    setBusy(false);
    if (createAdminBlock.rejected.match(result)) return void toast.error(t("admin.reports.actionFailed"));
    setBlock(result.payload);
    setAsking(false);
  };

  const unblock = async () => {
    if (!block) return;
    setBusy(true);
    const result = await dispatch(removeAdminBlock(block.id));
    setBusy(false);
    if (removeAdminBlock.rejected.match(result)) return void toast.error(t("admin.reports.actionFailed"));
    setBlock(null);
  };

  return (
    <>
      {block ? (
        <>
          <span className="text-sm text-red-700 dark:text-red-400" title={block.reason} data-testid="admin-blocked">
            {t("admin.blocks.blocked")}
          </span>
          <button type="button" className="btn btn-secondary flex items-center gap-1.5" disabled={busy} onClick={unblock}>
            <ShieldCheck size={16} aria-hidden="true" />
            {t("admin.blocks.unblock")}
          </button>
        </>
      ) : (
        <button type="button" className="btn btn-secondary flex items-center gap-1.5" disabled={busy} onClick={() => setAsking(true)}>
          <ShieldBan size={16} aria-hidden="true" />
          {t("admin.blocks.block")}
        </button>
      )}
      {asking && <AdminBlockDialog name={name} busy={busy} onConfirm={blockNow} onClose={() => setAsking(false)} />}
    </>
  );
};

export default AdminBlockToggle;
